import gothamBookFont from '@vaad1st/shared.theming/assets/fonts/gotham-book.woff2';
import gothamLightFont from '@vaad1st/shared.theming/assets/fonts/gotham-light.woff2';
import gothamMediumFont from '@vaad1st/shared.theming/assets/fonts/gotham-medium.woff2';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { getHostName } from 'src/utils/basepaths';
import { buildHreflangLinks } from 'src/utils/hreflang';
import { normaliseLlmArticleSchema } from 'src/utils/llm-schema';
import type { PageHeader } from 'src/utils/pageheader';
import type { CriticalImagePreload } from 'src/utils/performance';
import JsonLd from '../json-ld/json-ld.component';

export default function PageHead({
  title,
  description,
  canonicalUrl,
  brandName,
  favicon,
  faviconAppleTouch,
  favicon16,
  favicon32,
  faviconSafariPinned,
  appleTouchIconPrecomposed,
  translationsJS,
  preloadFiles,
  isHomepage = false,
  localeInfo = 'en-GB',
  hreflangLocales,
  hreflangPath,
  aemLastModifiedDate,
  regenerateTime,
  llmArticleSchema,
  criticalImagePreloads = [],
}: PageHeader & {
  criticalImagePreloads?: CriticalImagePreload[];
}) {
  const { basePath, defaultLocale } = useRouter();
  const withBasePath = (assetPath: string) => {
    if (!basePath) {
      return assetPath;
    }

    return assetPath.startsWith(basePath)
      ? assetPath
      : `${basePath}${assetPath}`;
  };

  if (typeof window !== 'undefined' && title) {
    let pageName = isHomepage ? 'Home Page' : title.trim();
    if (!isHomepage) {
      const firstPipeIndex = title.indexOf('|');
      if (firstPipeIndex !== -1) {
        pageName = title.substring(0, firstPipeIndex).trim();
      }
    }
    const pageType = isHomepage ? 'Home Page' : 'Content';

    let country = 'gb';
    let language = 'en';
    if (localeInfo.length === 5 && localeInfo.includes('-')) {
      country = localeInfo.split('-')[1]?.toLowerCase() ?? country;
      language = localeInfo.split('-')[0]?.toLowerCase() ?? language;
    }

    const data = {
      eventType: 'homepage_page_title',
      pageName,
      pageType,
      country,
      language,
    };

    window.postMessage(data, '*');
  }

  const articleSchema = normaliseLlmArticleSchema(llmArticleSchema);
  const hreflangLinks =
    hreflangLocales?.length && hreflangPath
      ? buildHreflangLinks(
          getHostName(),
          hreflangPath,
          hreflangLocales,
          defaultLocale,
        )
      : [];
  const lastModifiedContent = (() => {
    if (!aemLastModifiedDate) {
      return null;
    }

    const lastModifiedDate = new Date(aemLastModifiedDate);
    if (Number.isNaN(lastModifiedDate.getTime())) {
      return null;
    }

    // Use a deterministic UTC representation so server and client render the same value.
    return lastModifiedDate.toISOString();
  })();

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {hreflangLinks.map(({ hrefLang, href }) => (
          <link
            key={`hreflang-${hrefLang}`}
            rel="alternate"
            hrefLang={hrefLang}
            href={href}
          />
        ))}
        <meta name="author" content={brandName} />
        {lastModifiedContent && (
          <meta name="json-last-modified-date" content={lastModifiedContent} />
        )}
        {regenerateTime && <meta name="build-date" content={regenerateTime} />}
        <link rel="shortcut icon" href={favicon} />
        <link
          rel="apple-touch-icon-precomposed"
          sizes="114x114"
          href={appleTouchIconPrecomposed}
        />
        <link
          rel="apple-touch-icon-precomposed"
          href={appleTouchIconPrecomposed}
        />
        <link rel="apple-touch-icon" sizes="180x180" href={faviconAppleTouch} />
        <link rel="mask-icon" href={faviconSafariPinned} color="#da0530" />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
        {/* Preload Next JS fonts */}
        <link
          rel="preload"
          href={withBasePath(gothamBookFont)}
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        <link
          rel="preload"
          href={withBasePath(gothamLightFont)}
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        <link
          rel="preload"
          href={withBasePath(gothamMediumFont)}
          as="font"
          crossOrigin="anonymous"
          type="font/woff2"
        />
        {criticalImagePreloads.map(image => (
          <link
            key={[image.href, image.media ?? 'all'].join('-')}
            rel="preload"
            href={image.href}
            as="image"
            media={image.media}
            fetchPriority="high"
          />
        ))}
        {/* Preload files for header-footer */}
        {preloadFiles &&
          preloadFiles.map(file => (
            <link
              key={file.path}
              rel="preload"
              href={file.path}
              as={file.type}
            />
          ))}
        {articleSchema && llmArticleSchema?.enabled && (
          <JsonLd data={articleSchema} />
        )}
      </Head>
      {translationsJS?.id && translationsJS?.url && (
        <Script
          id="translations-js"
          type="text/javascript"
          referrerPolicy="no-referrer-when-downgrade"
          src={translationsJS.url}
          data-oljs={translationsJS.id}
          strategy="afterInteractive"
        />
      )}
    </>
  );
}
